import { useCallback } from 'react';

import { useCreateExternalDocument } from '@spotify/confidence-external-documents-react';
import { CollaborationV1ExternalDocumentSourceType } from '@spotify/confidence-graphql';

import {
  DocAttachment,
  PendingAttachment,
  ResolvedAttachment,
} from '../../types';

const EXTERNAL_DOCUMENT_SOURCE_TYPES: Record<
  PendingAttachment['sourceType'],
  CollaborationV1ExternalDocumentSourceType
> = {
  GOOGLE_DOCS: CollaborationV1ExternalDocumentSourceType.GoogleDocs,
  GOOGLE_SLIDES: CollaborationV1ExternalDocumentSourceType.GoogleSlides,
  NOTION: CollaborationV1ExternalDocumentSourceType.Notion,
};

export type UseResolveAttachmentsResult = {
  resolveAttachments: (
    chatName: string,
    attachments: DocAttachment[],
  ) => Promise<ResolvedAttachment[]>;
};

export const useResolveAttachments = (): UseResolveAttachmentsResult => {
  const { createExternalDocument } = useCreateExternalDocument();

  const resolveAttachments = useCallback(
    async (
      chatName: string,
      attachments: DocAttachment[],
    ): Promise<ResolvedAttachment[]> => {
      const resolved: ResolvedAttachment[] = [];
      for (const attachment of attachments) {
        if (attachment.kind === 'resolved') {
          resolved.push(attachment);
          continue;
        }
        const sourceType =
          EXTERNAL_DOCUMENT_SOURCE_TYPES[attachment.sourceType];
        const ref = await createExternalDocument({
          resource: chatName,
          title: attachment.title,
          source: {
            type: sourceType,
            sourceId: attachment.sourceId,
            uri: attachment.uri,
          },
        });
        resolved.push({
          kind: 'resolved',
          name: ref.name,
          title: ref.title ?? attachment.title,
          uri: ref.source?.uri ?? attachment.uri,
        });
      }
      return resolved;
    },
    [createExternalDocument],
  );

  return { resolveAttachments };
};
